/**
 * `UIImage` / `CGImage` bitmaps (`Image(uiImage:)`) on the page. Swift sends
 * each bitmap's source once (an `imagedata` op: an image file, or a pixel
 * buffer such as a QR code) and every `image` element that shows a bitmap
 * carries its recipe: the source's key and the Core Image steps to run
 * (docs/ops-protocol.md, "Bitmaps and Core Image"). A store keeps the
 * sources and draws recipes (src/bitmapStore.ts); this module puts each
 * drawing, at the size the image is shown, on the element's `<canvas>`.
 *
 * The store runs in a worker (src/bitmapWorker.ts) where the browser has
 * canvases there (`OffscreenCanvas`), so decoding, resizing and filters
 * leave the page's thread free for Swift; the drawing comes back as an
 * `ImageBitmap` the canvas shows without a copy. Elsewhere, or with
 * `?bitmaps=main` in the page's URL, the store runs on the page.
 */

import { BitmapStore, type Bitmaps, type RenderedBitmap } from './bitmapStore';
import type { BitmapWorkerReply, BitmapWorkerRequest } from './bitmapWorkerProtocol';
import { workingScale } from './imageOps';
import type { BitmapRecipe, ImageDataOp } from './protocol';

export { BitmapStore } from './bitmapStore';

/**
 * The store in the worker. Sources are sent as they arrive; until the worker
 * says it is running they are also kept here, so that a worker that cannot
 * start (a blocked script, no canvases there after all) hands everything to
 * a store on the page instead.
 */
class WorkerBitmaps implements Bitmaps {
  private sizes = new Map<string, { width: number; height: number }>();
  private pending = new Map<number, { resolve: (value: ImageBitmap | Blob | null) => void; reject: (error: Error) => void }>();
  private nextId = 1;
  private worker: Worker | null;
  /** Sources sent before the worker started (null once it has). */
  private early: ImageDataOp[] | null = [];
  /** The store on the page, when the worker failed to start. */
  private fallback: BitmapStore | null = null;

  constructor(worker: Worker) {
    this.worker = worker;
    worker.onmessage = (event: MessageEvent<BitmapWorkerReply>) => {
      const reply = event.data;
      if ('ready' in reply) {
        this.early = null;
        return;
      }
      const request = this.pending.get(reply.id);
      if (!request) return;
      this.pending.delete(reply.id);
      if (reply.ok) request.resolve(reply.value);
      else request.reject(new Error(reply.error));
    };
    worker.onerror = (event) => {
      event.preventDefault();
      if (this.early === null) return; // a running worker reports its errors per request
      console.warn(`[swiftbrowser] the bitmap worker did not start (${event.message || 'error'}); drawing bitmaps on the page`);
      this.useFallback();
    };
  }

  private useFallback(): void {
    const store = new BitmapStore();
    for (const op of this.early ?? []) store.add(op);
    this.early = null;
    this.fallback = store;
    this.worker?.terminate();
    this.worker = null;
    const failed = [...this.pending.values()];
    this.pending.clear();
    for (const request of failed) request.reject(new Error('the bitmap worker stopped'));
  }

  private send(request: BitmapWorkerRequest): void {
    this.worker!.postMessage(request);
  }

  private ask(request: { type: 'render'; recipe: BitmapRecipe; scale: number } | { type: 'png'; recipe: BitmapRecipe }): Promise<ImageBitmap | Blob | null> {
    const id = this.nextId++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.send({ ...request, id });
    });
  }

  add(op: ImageDataOp): void {
    if (this.sizes.has(op.key)) return;
    this.sizes.set(op.key, { width: Math.max(0, op.width | 0), height: Math.max(0, op.height | 0) });
    if (this.fallback) {
      this.fallback.add(op);
      return;
    }
    if (this.early) {
      this.early.push(op);
      this.send({ type: 'add', op });
      return;
    }
    // The bytes from linear memory are this op's own copy: moved, not copied.
    const bytes = op.data ?? op.pixels;
    this.worker!.postMessage({ type: 'add', op } satisfies BitmapWorkerRequest, typeof bytes === 'object' ? [bytes.buffer] : []);
  }

  has(key: string): boolean {
    return this.sizes.has(key);
  }

  sizeOf(key: string): { width: number; height: number } | undefined {
    return this.sizes.get(key);
  }

  async render(recipe: BitmapRecipe, scale: number): Promise<RenderedBitmap | null> {
    if (recipe.source === null) return null;
    if (this.fallback) return this.fallback.render(recipe, scale);
    if (!this.sizes.has(recipe.source)) throw new Error(`no image data for ${recipe.source}`);
    try {
      return (await this.ask({ type: 'render', recipe, scale })) as ImageBitmap | null;
    } catch (error) {
      // The worker failed to start while drawing: draw on the page.
      const fallback = this.fallback as BitmapStore | null;
      if (fallback) return fallback.render(recipe, scale);
      throw error;
    }
  }

  async png(recipe: BitmapRecipe): Promise<Blob | null> {
    if (recipe.source === null || !this.sizes.has(recipe.source)) return null;
    if (this.fallback) return this.fallback.png(recipe);
    return (await this.ask({ type: 'png', recipe })) as Blob | null;
  }
}

/** Whether to draw in a worker: the browser has worker canvases, and the page did not ask otherwise. */
function wantsWorker(): boolean {
  if (typeof Worker !== 'function' || typeof OffscreenCanvas !== 'function' || typeof createImageBitmap !== 'function') return false;
  try {
    return new URLSearchParams(location.search).get('bitmaps') !== 'main';
  } catch {
    return true;
  }
}

function makeBitmaps(): Bitmaps {
  if (!wantsWorker()) return new BitmapStore();
  try {
    return new WorkerBitmaps(new Worker(new URL('./bitmapWorker.ts', import.meta.url), { type: 'module', name: 'bitmaps' }));
  } catch {
    return new BitmapStore();
  }
}

/** The page's bitmaps: one store, as every source is named by its content. */
export const bitmapStore: Bitmaps = makeBitmaps();

/**
 * One `<canvas>` showing a recipe. It draws once both the recipe and the
 * laid-out size are known, again when either changes enough to matter, and
 * never two drawings at once: a request that arrives during a drawing waits
 * for it and takes the newest values. Until a drawing finishes the canvas
 * keeps the previous picture, so a slider dragging a filter never blanks it.
 */
class BitmapView {
  private recipe: BitmapRecipe | null = null;
  private recipeKey = '';
  private boxWidth = 0;
  private boxHeight = 0;
  private drawn = '';
  private busy = false;
  private again = false;

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly store: Bitmaps,
  ) {}

  setRecipe(recipe: BitmapRecipe): void {
    const key = JSON.stringify(recipe);
    if (key === this.recipeKey) return;
    this.recipe = recipe;
    this.recipeKey = key;
    this.update();
  }

  setBox(width: number, height: number): void {
    if (width === this.boxWidth && height === this.boxHeight) return;
    this.boxWidth = width;
    this.boxHeight = height;
    this.update();
  }

  private update(): void {
    const recipe = this.recipe;
    if (!recipe) return;
    if (this.busy) {
      this.again = true;
      return;
    }
    const source = recipe.source === null ? undefined : this.store.sizeOf(recipe.source);
    // Before layout, draw for the image's own size; layout refines it.
    const ratio = typeof devicePixelRatio === 'number' && devicePixelRatio > 0 ? devicePixelRatio : 1;
    const width = this.boxWidth > 0 ? this.boxWidth * ratio : recipe.width;
    const height = this.boxHeight > 0 ? this.boxHeight * ratio : recipe.height;
    const scale = workingScale(recipe.width, recipe.height, width, height, source ? source.width * source.height : 0);
    const key = `${this.recipeKey}@${scale}`;
    if (key === this.drawn) return;
    this.busy = true;
    this.store
      .render(recipe, scale)
      .then(
        (image) => {
          this.drawn = key;
          this.paint(image);
          this.canvas.dataset['sbBitmap'] = 'ready';
        },
        (error: unknown) => {
          this.drawn = key;
          this.paint(null);
          this.canvas.dataset['sbBitmap'] = 'error';
          console.warn(`[swiftbrowser] could not draw a bitmap: ${error instanceof Error ? error.message : String(error)}`);
        },
      )
      .finally(() => {
        this.busy = false;
        this.canvas.dispatchEvent(new CustomEvent('sb:bitmap', { bubbles: true }));
        if (this.again) {
          this.again = false;
          this.update();
        }
      });
  }

  private paint(image: RenderedBitmap | null): void {
    const canvas = this.canvas;
    if (!image) {
      canvas.width = 0;
      canvas.height = 0;
      return;
    }
    if (canvas.width !== image.width) canvas.width = image.width;
    if (canvas.height !== image.height) canvas.height = image.height;
    if (isImageBitmap(image)) {
      // From the worker: handed to the canvas as it is, without a copy.
      const renderer = canvas.getContext('bitmaprenderer');
      if (renderer) {
        renderer.transferFromImageBitmap(image);
        return;
      }
      canvas.getContext('2d')?.drawImage(image, 0, 0);
      image.close();
      return;
    }
    const context = canvas.getContext('2d');
    if (!context) return;
    if (image instanceof ImageData) {
      context.putImageData(image, 0, 0);
    } else {
      context.clearRect(0, 0, image.width, image.height);
      context.drawImage(image.canvas, 0, 0);
    }
  }
}

function isImageBitmap(image: RenderedBitmap): image is ImageBitmap {
  return typeof ImageBitmap === 'function' && image instanceof ImageBitmap;
}

const views = new WeakMap<HTMLCanvasElement, BitmapView>();

/** Shows `recipe` on `canvas` (drawn once the canvas is laid out). */
export function showBitmap(canvas: HTMLCanvasElement, recipe: BitmapRecipe, store: Bitmaps = bitmapStore): void {
  let view = views.get(canvas);
  if (!view) {
    view = new BitmapView(canvas, store);
    views.set(canvas, view);
  }
  view.setRecipe(recipe);
}

/** The layout engine placed the `image` element `el` at `width` × `height` points. */
export function bitmapLaidOut(el: HTMLElement, width: number, height: number): void {
  const canvas = el.firstElementChild;
  if (!(canvas instanceof HTMLCanvasElement)) return;
  views.get(canvas)?.setBox(width, height);
}
